/**
 * Bounded classifiers for INTERNAL_LINK_CLICK.
 *
 * Internal navigation was invisible: the only click events we had were a handful of
 * named CTAs (NEXT_TOOL_CLICKED, SHARE_CLICKED, ...), so an ordinary link — the
 * ad-free offer, a footer link, a related-tool link — produced no signal at all.
 *
 * The hard constraint is `metric-attrs.ts`: an attribute whose values are URLs is a
 * bug, because it blows up Mongo cardinality (METRICS.md §3). A site with ~5,900 pages
 * cannot ship the href. So both attributes below collapse to a small closed set, and
 * the *source* page needs no attribute at all — MetricsService.emit already stamps
 * `pageUrl` on every event.
 *
 * Prometheus is unaffected either way: it only carries {event, platform}, so this adds
 * exactly one new series per platform. The attributes are for the Mongo drill-down.
 */

/** Destination classes. Closed set — add a case here, never a raw path. */
export type LinkDest =
    | 'home' | 'product' | 'portal' | 'docs' | 'blog' | 'converter' | 'compress'
    | 'editor' | 'hub' | 'legal' | 'downloads' | 'other';

/** Where the link sits on the page. Closed set. */
export type LinkLocation =
    | 'header' | 'footer' | 'ad_offer' | 'related' | 'content' | 'other';

/** `other` is the catch-all for both sets, mirroring `OTHER_EXT` in metric-attrs. */
export const OTHER_LINK = 'other';

/**
 * Classify a same-origin path into one destination bucket.
 *
 * Order matters: the most specific prefix wins, and `hub` is checked after the
 * `convert-`/`compress-` prefixes because `/mp4-converter` is a hub while
 * `/convert-mp4-to-mp3` is a converter page.
 */
export function linkDest(path: string): LinkDest {
    if (typeof path !== 'string' || !path) return OTHER_LINK;
    const p = path.split(/[?#]/)[0].toLowerCase().replace(/\/+$/, '');
    if (p === '' || p === '/') return 'home';
    if (p.startsWith('/product')) return 'product';
    if (p.startsWith('/portal')) return 'portal';
    if (p.startsWith('/docs')) return 'docs';
    if (p.startsWith('/blog')) return 'blog';
    if (p.startsWith('/downloads')) return 'downloads';
    if (p === '/privacy-policy' || p === '/terms-of-service') return 'legal';
    if (p.startsWith('/convert-')) return 'converter';
    if (p.startsWith('/compress')) return 'compress';
    if (/-editor$/.test(p) || p.startsWith('/edit-')) return 'editor';
    if (/-converter$/.test(p)) return 'hub';
    return OTHER_LINK;
}

/** Selectors are checked in order; the first match wins. */
const LOCATION_RULES: ReadonlyArray<readonly [string, LinkLocation]> = [
    ['[class*="upgrade-offer"]', 'ad_offer'],
    ['footer', 'footer'],
    ['header, nav', 'header'],
    ['[class*="related"], [class*="next-tool"]', 'related'],
    ['article, main, [class*="markdown"]', 'content'],
];

/** Classify where an anchor lives, walking up from the anchor itself. */
export function linkLocation(el: Element | null): LinkLocation {
    if (!el || typeof (el as Element).closest !== 'function') return OTHER_LINK;
    for (const [selector, name] of LOCATION_RULES) {
        try {
            if (el.closest(selector)) return name;
        } catch {
            // a bad selector must never break a click handler
        }
    }
    return OTHER_LINK;
}

/**
 * Bounded attributes for one anchor, or `null` when the click is not an internal
 * navigation we want to count: external hosts, non-http schemes (mailto/tel), pure
 * `#` fragments, and downloads all return null rather than a bogus bucket.
 */
export function internalLinkAttrs(
    anchor: { getAttribute(name: string): string | null; closest?: unknown } | null,
    currentHost: string,
): { dest: LinkDest; location: LinkLocation } | null {
    if (!anchor) return null;
    const href = anchor.getAttribute('href');
    if (!href) return null;
    if (anchor.getAttribute('download') !== null) return null;

    let path: string;
    if (href.startsWith('/')) {
        path = href;
    } else if (/^https?:\/\//i.test(href)) {
        try {
            const u = new URL(href);
            if (u.host !== currentHost) return null;   // external — not ours to count
            path = u.pathname + u.search;
        } catch {
            return null;
        }
    } else {
        return null;                                    // mailto:, tel:, #frag, relative
    }
    return { dest: linkDest(path), location: linkLocation(anchor as unknown as Element) };
}
